import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { MaintenanceIntervalType } from '../../../../../types/Enums';
import { translateIntervalType } from '../../../../../Helpers/maintenanceCostUtils';
import { translate } from '../../../../../types/EnumTranslations';
import { IntervalSettingsContext } from '../../hooks/useIntervalModal';
import NumberInput from '../../../../../Components/NumberInput';

interface IntervalSettingsModalProps {
    context: IntervalSettingsContext;
    onSave: (
        intervalValue: number,
        intervalType: MaintenanceIntervalType,
        useTemplate: boolean
    ) => void;
    onClose: () => void;
}

/**
 * Modal for configuring maintenance activity interval settings.
 * Allows setting a custom interval or restoring to template default.
 *
 * Note: State is initialized from props. The parent should use a key prop
 * to force remount when context changes (e.g., key={context.templateId}).
 */
export const IntervalSettingsModal: React.FC<IntervalSettingsModalProps> = ({
    context,
    onSave,
    onClose
}) => {
    const {
        templateName,
        templateIntervalValue,
        templateIntervalType,
        cardinalDirection,
        hasDirectionalInterval,
        currentIntervalValue,
        currentIntervalType,
        isCustomInterval
    } = context;

    const [intervalValue, setIntervalValue] = useState<number>(currentIntervalValue);
    const [intervalType, setIntervalType] = useState<MaintenanceIntervalType>(currentIntervalType);
    const [useTemplate, setUseTemplate] = useState<boolean>(!isCustomInterval);

    const handleSave = () => {
        onSave(intervalValue, intervalType, useTemplate);
    };

    const handleRestoreDefault = () => {
        setIntervalValue(templateIntervalValue);
        setIntervalType(templateIntervalType);
        setUseTemplate(true);
    };

    const handleIntervalValueChange = (value: number) => {
        setIntervalValue(Math.max(1, value));
        setUseTemplate(false);
    };

    const handleIntervalTypeChange = (type: MaintenanceIntervalType) => {
        setIntervalType(type);
        setUseTemplate(false);
    };

    const isModified = !useTemplate && (
        intervalValue !== templateIntervalValue ||
        intervalType !== templateIntervalType
    );

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
                    <div className="modal-content">
                        {/* Header */}
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Intervall - {templateName}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Stäng"
                            />
                        </div>

                        {/* Body */}
                        <div className="modal-body">
                            {/* Template default info */}
                            <div className="mb-4">
                                <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                    {hasDirectionalInterval && cardinalDirection
                                        ? `Mallens intervall för väderstreck ${translate(cardinalDirection)}:`
                                        : 'Mallens standardintervall:'}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{ fontWeight: 500 }}>
                                        {templateIntervalValue} {translateIntervalType(templateIntervalType)}
                                    </span>
                                    {isModified && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                            onClick={handleRestoreDefault}
                                            style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                        >
                                            <RefreshCw size={12} />
                                            Återställ
                                        </button>
                                    )}
                                </div>
                                {cardinalDirection && !hasDirectionalInterval && (
                                    <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                        Mallen har inget eget intervall för väderstreck {translate(cardinalDirection)}.
                                    </div>
                                )}
                            </div>

                            {/* Custom interval inputs */}
                            <div className="mb-3">
                                <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                    Anpassat intervall
                                </label>
                                <div className="d-flex gap-2">
                                    <NumberInput
                                        className="form-control"
                                        integer
                                        emptyValue={1}
                                        value={intervalValue}
                                        onChange={handleIntervalValueChange}
                                        min="1"
                                        max="1000"
                                        style={{ maxWidth: '100px' }}
                                    />
                                    <select
                                        className="form-select"
                                        value={intervalType}
                                        onChange={(e) => handleIntervalTypeChange(e.target.value as MaintenanceIntervalType)}
                                        style={{ maxWidth: '140px' }}
                                    >
                                        <option value={MaintenanceIntervalType.days}>dagar</option>
                                        <option value={MaintenanceIntervalType.weeks}>veckor</option>
                                        <option value={MaintenanceIntervalType.months}>månader</option>
                                        <option value={MaintenanceIntervalType.years}>år</option>
                                    </select>
                                </div>
                                {isModified && (
                                    <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                                        Detta kommer att använda ett anpassat intervall för denna komponent.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Avbryt
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                            >
                                Spara
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
